import React from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SignedImage from '../common/SignedImage';

const SectionShell = ({ title, subtitle, action, children }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 4,
      p: { xs: 2, md: 2.5 },
      borderColor: alpha('#0f172a', 0.08),
      boxShadow: '0 18px 45px -30px rgba(15,23,42,0.28)',
    }}
  >
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action || null}
    </Stack>
    {children}
  </Paper>
);

const MetaItem = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha('#2563eb', 0.09),
        color: '#2563eb',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  </Stack>
);

const StatCard = ({ label, value, tone = '#2563eb' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.75,
      borderRadius: 1.5,
      borderColor: alpha(tone, 0.18),
      background: `linear-gradient(180deg, ${alpha(tone, 0.10)} 0%, ${alpha('#ffffff', 0.96)} 100%)`,
      minHeight: 96,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.6, fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>
      {value || 'N/A'}
    </Typography>
  </Paper>
);

export const EmployeeHeroSection = ({
  employee,
  roleLabel,
  uploadAction,
  resetAction,
  editAction,
  uploadLoading,
  resetLoading,
  canResetPassword,
}) => {
  const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || 'Employee';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 5,
        borderColor: alpha('#0f172a', 0.08),
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 42%, #eff6ff 100%)',
        boxShadow: '0 24px 60px -36px rgba(15,23,42,0.28)',
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Avatar
              sx={{
                width: 84,
                height: 108,
                borderRadius: 1.5,
                bgcolor: alpha('#2563eb', 0.14),
                color: '#2563eb',
                border: `3px solid ${alpha('#ffffff', 0.9)}`,
                boxShadow: '0 14px 30px -22px rgba(37,99,235,0.65)',
                overflow: 'hidden',
              }}
            >
              {employee?.imageUrl ? (
                <SignedImage
                  src={employee.imageUrl}
                  alt={fullName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <PersonIcon sx={{ fontSize: 42 }} />
              )}
            </Avatar>
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label={employee?.employeeCode || 'No code'}
                  sx={{ fontWeight: 700, bgcolor: alpha('#0f172a', 0.05) }}
                />
                <Chip
                  size="small"
                  label={employee?.designation || 'Designation not set'}
                  sx={{ fontWeight: 700, bgcolor: alpha('#2563eb', 0.10), color: '#1d4ed8' }}
                />
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.08 }}>
                {fullName}
              </Typography>
              <Typography sx={{ mt: 0.75, color: 'text.secondary', maxWidth: 700 }}>
                Employee profile, identity records, bank setup, and salary setup from one screen.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                <MetaItem icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />} label="Role" value={roleLabel} />
                <MetaItem icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />} label="Phone" value={employee?.phone} />
                <MetaItem icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />} label="Email" value={employee?.email} />
              </Stack>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <Button
              component="label"
              variant="outlined"
              disabled={uploadLoading}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 700 }}
            >
              {uploadAction}
            </Button>
            {canResetPassword ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={resetAction}
                disabled={resetLoading}
                sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 700 }}
              >
                Reset Password
              </Button>
            ) : null}
            <Button
              variant="contained"
              onClick={editAction}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 700, boxShadow: 'none' }}
            >
              Edit Employee
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard label="Employee Code" value={employee?.employeeCode} tone="#2563eb" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard label="Date of Joining" value={employee?.dateOfJoiningLabel} tone="#ea580c" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard label="Department" value={employee?.department || employee?.role || 'N/A'} tone="#7c3aed" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard label="Status" value={employee?.statusLabel || employee?.status} tone="#059669" />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};

export const EmployeeProfileTableSection = ({ rows }) => (
  <SectionShell title="Profile Snapshot" subtitle="Core employee information kept easy to scan.">
    <Grid container spacing={1.5}>
      {rows.map((row) => (
        <Grid item xs={12} md={6} lg={4} key={row.label}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              borderColor: alpha('#0f172a', 0.08),
              minHeight: 92,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {row.label}
            </Typography>
            <Typography sx={{ mt: 0.7, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>
              {row.value || 'N/A'}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </SectionShell>
);

export const EmployeeWorkSummarySection = ({ employee }) => (
  <SectionShell title="Work Summary" subtitle="Small operational details that school admins usually need first.">
    <Grid container spacing={1.5}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, height: '100%', borderColor: alpha('#0f172a', 0.08) }}>
          <Stack spacing={1.5}>
            <MetaItem icon={<WorkOutlineOutlinedIcon sx={{ fontSize: 18 }} />} label="Designation" value={employee?.designation} />
            <MetaItem icon={<ApartmentOutlinedIcon sx={{ fontSize: 18 }} />} label="Department / Role" value={employee?.department || employee?.role} />
            <MetaItem icon={<SchoolOutlinedIcon sx={{ fontSize: 18 }} />} label="School" value={employee?.schoolName || employee?.school || 'Current school'} />
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, height: '100%', borderColor: alpha('#0f172a', 0.08) }}>
          <Stack spacing={1.5}>
            <MetaItem icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />} label="Joined On" value={employee?.dateOfJoiningLabel} />
            <MetaItem icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />} label="Date of Birth" value={employee?.dateOfBirthLabel} />
            <MetaItem icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Emergency Contact" value={employee?.emergencyContact} />
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  </SectionShell>
);

export const EmbeddedSection = ({ title, subtitle, children }) => (
  <SectionShell title={title} subtitle={subtitle}>
    <Divider sx={{ mb: 2, borderColor: alpha('#0f172a', 0.08) }} />
    {children}
  </SectionShell>
);
